/**
 * "Add DIGU to your home screen" (D-213). The site is already an installable web app (D-082); this decides whether to
 * offer it. Android Chrome fires `beforeinstallprompt`, which we keep so one tap shows the browser's own install
 * dialog. iPhone and iPad have no such event, so there we show the Share › Add to Home Screen steps. Never offered
 * inside the store apps (D-126), once DIGU already runs from the home screen, or on a computer.
 */
import { useEffect, useState } from "react";
import { isNativeApp } from "./native";

interface InstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> }

const KEY = "digu.installOffer";
const EVENT = "digu:install";
/** A "Not now" keeps the card away this long. */
export const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

let deferred: InstallPromptEvent | null = null;
let installed = false;
/** Kept in memory too, so Not now works where the browser blocks storage. */
let snoozedAt = 0;

/** Called once at start-up (main.tsx), before the browser can fire the event. */
export function listenForInstall(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e as InstallPromptEvent; window.dispatchEvent(new Event(EVENT)); });
  window.addEventListener("appinstalled", () => { installed = true; deferred = null; window.dispatchEvent(new Event(EVENT)); });
}

export const isStandalone = (): boolean =>
  typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

export const isAppleTouch = (ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints): boolean =>
  /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);

const isTouch = (): boolean => typeof window !== "undefined" && (window.matchMedia?.("(pointer: coarse)").matches ?? false);

function snoozed(now: number): boolean {
  let t = snoozedAt;
  try { t = Math.max(t, Number(localStorage.getItem(KEY)) || 0); } catch { /* private window */ }
  return t > 0 && now - t < SNOOZE_MS;
}

export type InstallMode = "prompt" | "ios" | null;

/** How DIGU can be added here, or null when there is nothing to offer. */
export function installMode(now = Date.now()): InstallMode {
  if (typeof window === "undefined" || installed || isNativeApp() || isStandalone() || snoozed(now)) return null;
  // Phones and tablets only: desktop Chrome fires the event too, but the card is about the home screen.
  if (!isTouch()) return null;
  if (deferred) return "prompt";
  if (isAppleTouch()) return "ios";
  return null;
}

export function snoozeInstall(now = Date.now()): void {
  snoozedAt = now;
  try { localStorage.setItem(KEY, String(now)); } catch { /* private window */ }
  window.dispatchEvent(new Event(EVENT));
}

/** Shows the browser's install dialog (Android). True when the player said yes. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === "accepted") installed = true;
  window.dispatchEvent(new Event(EVENT));
  return outcome === "accepted";
}

export function useInstallMode(): InstallMode {
  const [mode, setMode] = useState<InstallMode>(() => installMode());
  useEffect(() => { const on = () => setMode(installMode()); window.addEventListener(EVENT, on); return () => window.removeEventListener(EVENT, on); }, []);
  return mode;
}

/** Test hook: forget the kept event. */
export function resetInstallForTests(): void { deferred = null; installed = false; snoozedAt = 0; }
